import { triageTotals, type Decision, type TriageItem } from "@paredown/shared";
import { useCallback, useEffect, useState, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { money, type Api, type RecurringResponse } from "../api.js";
import { labelFor, labelTokens } from "../vault.js";
import { CardView } from "./CardView.js";

const FLAG_LABEL = { duplicate: "charged twice", price_increase: "price went up", stopped: "stopped charging" } as const;
const PANES: Array<{ id: Decision; title: string; totalLabel: string }> = [
  { id: "cancel", title: "Cancel", totalLabel: "saved per year" },
  { id: "keep", title: "Keep", totalLabel: "kept per year" },
  { id: "unsure", title: "Not sure", totalLabel: "per year" },
];
const CHOICES: Array<[Decision, string]> = [
  ["keep", "Keep"],
  ["cancel", "Cancel"],
  ["unsure", "Not sure"],
];

const nameOf = (g: TriageItem) => g.suggestion?.service ?? g.merchant;

function Choices({ item, onDecide }: { item: TriageItem; onDecide: (d: Decision | null) => void }) {
  return (
    <div className="choices" role="group" aria-label={`Decide on ${nameOf(item)}`}>
      {CHOICES.map(([d, label]) => (
        <button
          key={d}
          type="button"
          className={`choice ${d}${item.decision === d ? " active" : ""}${!item.decision && item.suggestion?.suggestion === d ? " suggested" : ""}`}
          aria-pressed={item.decision === d}
          onClick={() => onDecide(item.decision === d ? null : d)}
          data-testid={`choose-${d}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Summary({ item, overlapNames }: { item: TriageItem; overlapNames: string[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="summary">
      <div className="line">
        <button type="button" className="link name" onClick={() => setOpen(!open)} aria-expanded={open}>
          {nameOf(item)}
        </button>
        <span className="num">{money(item.yearlyCostCents)}/yr</span>
      </div>
      <div className="muted small">
        {money(item.lastAmountCents)} {item.cadence}
        {item.platform !== "direct" ? ` via ${item.platform}` : ""} · {item.accounts.map((a) => labelFor(a)).join(", ")}
        {item.flags.map((f) => (
          <span key={f} className={`badge ${f}`}>
            {FLAG_LABEL[f]}
          </span>
        ))}
        {overlapNames.length > 0 && <span className="badge overlap">overlaps with {overlapNames.join(", ")}</span>}
      </div>
      {item.suggestion && (
        <div className="suggestion small" data-testid="suggestion">
          Suggest <strong>{item.suggestion.suggestion === "unsure" ? "not sure" : item.suggestion.suggestion}</strong>:{" "}
          {item.suggestion.reason}
        </div>
      )}
      {open && (
        <ul className="charges small">
          {item.charges.map((c, i) => (
            <li key={i}>
              {c.date} · {labelTokens(c.rawDescription)} · {money(c.amountCents)} · {labelFor(c.accountToken)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Recurring({ api, accountEmail }: { api: Api; accountEmail?: string }) {
  const [data, setData] = useState<RecurringResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Decision>("cancel");
  const [dragOver, setDragOver] = useState<Decision | null>(null);

  const refresh = useCallback(() => {
    api
      .recurring()
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, [api]);
  useEffect(refresh, [refresh]);

  // Poll while suggestions or cards are being written.
  const working = !!data && (data.suggest.status === "running" || data.groups.some((g) => g.card?.status === "pending"));
  useEffect(() => {
    if (!working) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [working, refresh]);

  if (error) return <p className="error">{error}</p>;
  if (!data) return <p>Loading…</p>;

  const byKey = new Map(data.groups.map((g) => [g.key, g]));
  const overlapNames = (g: TriageItem) =>
    (g.suggestion?.overlapsWith ?? []).map((k) => byKey.get(k)).filter((x): x is TriageItem => !!x).map(nameOf);
  const totals = triageTotals(data.groups);
  const undecided = data.groups.filter((g) => !g.decision);
  const ROUTE_ORDER = ["web_toggle", "billing_platform", "email", "phone", "card_block"];
  const ease = (g: TriageItem) => (g.card?.card ? ROUTE_ORDER.indexOf(g.card.card.route) : ROUTE_ORDER.length);
  // The Cancel pane is the action plan: easiest route first, then biggest saving.
  const inPane = (d: Decision) =>
    data.groups
      .filter((g) => g.decision === d)
      .sort((a, b) => (d === "cancel" ? ease(a) - ease(b) : 0) || b.yearlyCostCents - a.yearlyCostCents);
  const paneTotal = { cancel: totals.cancelCents, keep: totals.keptCents, unsure: totals.unsureCents };

  async function decide(key: string, decision: Decision | null) {
    // Move it right away; the server answers with the card status for Cancel.
    setData((d) =>
      d && {
        ...d,
        groups: d.groups.map((g) =>
          g.key === key
            ? { ...g, decision, card: decision === "cancel" ? (g.card ?? { status: "pending", updatedAt: "" }) : g.card }
            : g,
        ),
      },
    );
    try {
      const r = await api.decide(key, decision);
      if (r.card) setData((d) => d && { ...d, groups: d.groups.map((g) => (g.key === key ? { ...g, card: r.card } : g)) });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      refresh();
    }
  }

  async function suggest() {
    setData((d) => d && { ...d, suggest: { status: "running" } });
    await api.suggest().catch((e: Error) => setError(e.message));
    refresh();
  }

  async function regenerate(key: string) {
    setData((d) => d && { ...d, groups: d.groups.map((g) => (g.key === key ? { ...g, card: { status: "pending", updatedAt: "" } } : g)) });
    await api.regenerateCard(key).catch((e: Error) => setError(e.message));
    refresh();
  }

  const onDragStart = (e: DragEvent, key: string) => e.dataTransfer.setData("text/plain", key);
  const dropProps = (d: Decision) => ({
    onDragOver: (e: DragEvent) => {
      e.preventDefault();
      setDragOver(d);
    },
    onDragLeave: () => setDragOver(null),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      setDragOver(null);
      const key = e.dataTransfer.getData("text/plain");
      if (key && byKey.get(key)?.decision !== d) void decide(key, d);
    },
  });

  const suggestLabel =
    data.suggest.status === "running"
      ? "Reviewing your charges…"
      : data.groups.some((g) => g.suggestion)
        ? "Refresh suggestions"
        : "Get suggestions";

  return (
    <section className="triage">
      <h2>Recurring charges</h2>
      <p>
        {data.groups.length} recurring charges across {data.statementCount} statements, about{" "}
        <strong>{money(totals.cancelCents + totals.keptCents + totals.unsureCents + totals.undecidedCents)}</strong> a year.
      </p>
      {data.pendingReviewCount > 0 && (
        <p className="notice">
          {data.pendingReviewCount} statement{data.pendingReviewCount === 1 ? "" : "s"} still{" "}
          {data.pendingReviewCount === 1 ? "needs" : "need"} a look and {data.pendingReviewCount === 1 ? "isn't" : "aren't"} counted yet.{" "}
          <Link to="/statements">Review</Link>
        </p>
      )}
      {data.statementCount === 0 && <p>Upload some statements first.</p>}

      {data.groups.length > 0 && (
        <>
          <p>
            <button type="button" onClick={() => void suggest()} disabled={data.suggest.status === "running"} data-testid="suggest">
              {suggestLabel}
            </button>{" "}
            {data.suggest.status === "failed" && <span className="error small">Last run failed: {data.suggest.error}</span>}
            {data.suggest.status !== "running" && !data.groups.some((g) => g.suggestion) && (
              <span className="muted small">Each charge gets a suggested Keep or Cancel with a reason. You decide.</span>
            )}
          </p>

          <div className="totals-bar" data-testid="totals">
            <span>
              <strong>{money(totals.cancelCents)}</strong> saved/yr
            </span>
            <span>{money(totals.keptCents)} kept</span>
            <span>{money(totals.unsureCents)} not sure</span>
            <span>{money(totals.undecidedCents)} undecided</span>
          </div>

          {undecided.length > 0 && (
            <div className="queue">
              <h3>To decide ({undecided.length})</h3>
              <ul className="items">
                {undecided.map((g) => (
                  <li key={g.key} draggable onDragStart={(e) => onDragStart(e, g.key)} data-testid="undecided-item">
                    <Summary item={g} overlapNames={overlapNames(g)} />
                    <Choices item={g} onDecide={(d) => void decide(g.key, d)} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="tabs" role="tablist">
            {PANES.map((p) => (
              <button
                key={p.id}
                role="tab"
                type="button"
                aria-selected={tab === p.id}
                className={tab === p.id ? "active" : ""}
                onClick={() => setTab(p.id)}
              >
                {p.title} · {money(paneTotal[p.id])}
              </button>
            ))}
          </div>
          <div className="panes">
            {PANES.map((p) => {
              const items = inPane(p.id);
              return (
                <div
                  key={p.id}
                  className={`pane ${p.id}${tab === p.id ? " current" : ""}${dragOver === p.id ? " over" : ""}`}
                  data-testid={`pane-${p.id}`}
                  {...dropProps(p.id)}
                >
                  <div className="pane-head">
                    <h3>
                      {p.title} ({items.length})
                    </h3>
                    <span>
                      <strong>{money(paneTotal[p.id])}</strong> {p.totalLabel}
                    </span>
                  </div>
                  <ul className="items">
                    {items.length === 0 && <li className="muted small">Nothing here yet.</li>}
                    {items.map((g) => (
                      <li key={g.key} draggable onDragStart={(e) => onDragStart(e, g.key)}>
                        <Summary item={g} overlapNames={overlapNames(g)} />
                        <Choices item={g} onDecide={(d) => void decide(g.key, d)} />
                        {p.id === "cancel" && (
                          <CardView item={g} accountEmail={accountEmail} onRegenerate={() => void regenerate(g.key)} />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
